import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import platformAPI from "../../../services/platformApi";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import LandingNav from "./LandingNav";
import s from "./homeLanding.module.css";

const FEATURE_HIGHLIGHTS = [
  {
    icon: "📅",
    title: "Календарь и журнал",
    text: "Расписание групп, отметка посещаемости и списание с абонемента в одно касание.",
  },
  {
    icon: "👥",
    title: "Клиенты и абонементы",
    text: "Карточки спортсменов, типы абонементов, оплаты и контроль должников.",
  },
  {
    icon: "📊",
    title: "Аналитика клуба",
    text: "Выручка, посещаемость, рейтинг групп — решения на основе цифр.",
  },
  {
    icon: "📱",
    title: "Кабинет клиента",
    text: "PWA для спортсменов: расписание, оплаты, лента клуба и push-уведомления.",
  },
  {
    icon: "🏋️",
    title: "Кабинет тренера",
    text: "Отдельный портал для тренеров с расписанием, журналом и группами.",
  },
  {
    icon: "⚡",
    title: "Быстрый старт",
    text: `Регистрация клуба за минуты — ${TRIAL_DAYS} дней демо-доступа без оплаты.`,
  },
];

const STEPS = [
  {
    title: "Регистрация клуба",
    text: `Зарегистрируйтесь и получите ${TRIAL_DAYS} дней демо-доступа ко всем функциям CRM.`,
  },
  {
    title: "Настройте направления",
    text: "Добавьте тренеров, группы и типы абонементов под ваш формат.",
  },
  {
    title: "Ведите занятия",
    text: "Отмечайте посещаемость, принимайте оплаты и держите клиентов в курсе.",
  },
];

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

function planHighlights(features = {}) {
  const items = [];
  if (features.journal) items.push("Журнал посещаемости");
  if (features.finance) items.push("Финансы и оплаты");
  if (features.analytics) items.push("Аналитика");
  if (features.trainer_portal) items.push("Кабинет тренера");
  if (features.polls) items.push("Опросы и рассылки");
  if (features.competitions) items.push("Соревнования");
  if (features.max_trainers) items.push(`До ${features.max_trainers} тренеров`);
  if (features.max_clients) items.push(`До ${features.max_clients} клиентов`);
  return items.slice(0, 6);
}

export default function HomeLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => setPlans(r.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const featuredCode = useMemo(() => {
    const withBadge = plans.find((p) => p.badge);
    return withBadge?.code || plans[1]?.code || plans[0]?.code;
  }, [plans]);

  return (
    <div className={`${s.page} ${s.pageLanding}`}>
      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <div className={s.sessionActions}>
            <a
              href={buildClinicPortalUrl(
                tenant.subdomain,
                getDashboardPath(role),
              )}
              className={`${s.btn} ${s.btnPrimary}`}
            >
              Открыть кабинет
            </a>
            <Link to="/register-clinic?new=1" className={`${s.btn} ${s.btnGhost}`}>
              Новый клуб
            </Link>
          </div>
        </div>
      ) : null}

      <LandingNav mode="platform" />

      <section className={s.hero}>
        <div>
          <div className={s.eyebrow}>
            CRM для спорта · PWA для клиентов ·{" "}
            <span className={s.trialHighlight}>{TRIAL_DAYS} дней бесплатно</span>
          </div>
          <h1 className={s.title}>
            Управляйте клубом
            <br />
            <span className={s.titleAccent}>без хаоса в таблицах</span>
          </h1>
          <p className={s.lead}>
            {L.productName} — платформа для тренеров и спортивных секций:
            расписание, журнал, абонементы, финансы и личные кабинеты в одной
            системе.
          </p>
          <div className={s.heroActions}>
            <Link to="/register-clinic" className={`${s.btn} ${s.btnPrimary}`}>
              Попробовать {TRIAL_DAYS} дней бесплатно
            </Link>
            <Link to="/find" className={`${s.btn} ${s.btnOutline}`}>
              Найти свой клуб
            </Link>
          </div>
          <p className={s.trialNote}>
            Зарегистрируйте клуб — получите полный демо-доступ на {TRIAL_DAYS}{" "}
            дней. Без карты, без обязательств.
          </p>
          <div className={s.metrics}>
            <div className={s.metric}>
              <strong>1 клик</strong>
              <span>отметка в журнале</span>
            </div>
            <div className={s.metric}>
              <strong>PWA</strong>
              <span>кабинет на телефоне</span>
            </div>
            <div className={s.metric}>
              <strong>24/7</strong>
              <span>доступ к расписанию</span>
            </div>
          </div>
        </div>

        <div className={s.preview}>
          <div className={s.previewHeader}>
            <strong>Сводка «Сегодня»</strong>
            <div className={s.previewDots} aria-hidden>
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className={s.previewCard}>
            <strong>Ближайшее занятие · 18:00</strong>
            <p>Группа U12 — зал №2. Журнал открыт, 14 записано.</p>
          </div>
          <div className={s.previewCard}>
            <strong>Оплаты за день</strong>
            <p>3 абонемента продлены, 1 должник — напоминание отправлено.</p>
          </div>
          <div className={s.previewCard}>
            <strong>Рейтинг посещаемости</strong>
            <p>Спортсмен видит место в группе, тренер — динамику за месяц.</p>
          </div>
        </div>
      </section>

      <section id="features" className={`${s.section} ${s.sectionAlt}`}>
        <h2 className={s.sectionTitle}>Всё для работы клуба</h2>
        <p className={s.sectionLead}>
          От одиночного тренера до секции с командой — инструменты под ваш
          масштаб, без лишней бюрократии.
        </p>
        <div className={s.features}>
          {FEATURE_HIGHLIGHTS.map((f) => (
            <article key={f.title} className={s.feature}>
              <div className={s.featureIcon}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Как это работает</h2>
        <p className={s.sectionLead}>
          Три шага от регистрации до первого занятия в системе.
        </p>
        <div className={s.steps}>
          {STEPS.map((step, i) => (
            <article key={step.title} className={s.step}>
              <div className={s.stepNum}>Шаг {i + 1}</div>
              <h3>{step.title}</h3>
              <p style={{ margin: 0, color: "#9aa3b5" }}>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="pricing" className={`${s.section} ${s.sectionAlt}`}>
        <h2 className={s.sectionTitle}>Тарифы BaySport</h2>
        <p className={s.sectionLead}>
          Прозрачные планы без скрытых модулей. После регистрации —{" "}
          {TRIAL_DAYS} дней демо-доступа ко всем функциям тарифа.
        </p>
        {plansLoading ? (
          <p className={s.loading}>Загрузка тарифов…</p>
        ) : plans.length ? (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardFeatured : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3 className={s.priceName}>{plan.name}</h3>
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small> / мес</small>
                </p>
                {plan.yearly_price_rub ? (
                  <p style={{ margin: 0, fontSize: 13, color: "#8b95a8" }}>
                    или {formatRub(plan.yearly_price_rub)} ₽ / год
                  </p>
                ) : null}
                <p className={s.priceDesc}>{plan.description}</p>
                <ul className={s.priceFeatures}>
                  {planHighlights(plan.features).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/register-clinic"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnPrimary : s.btnOutline
                  }`}
                  style={{ width: "100%" }}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        ) : (
          <p className={s.loading}>
            Тарифы скоро появятся.{" "}
            <Link to="/register-clinic" style={{ color: "#4ade80" }}>
              Зарегистрировать клуб
            </Link>
          </p>
        )}
      </section>

      <section className={s.cta}>
        <div className={s.ctaBox}>
          <h2 className={s.sectionTitle} style={{ marginBottom: 12 }}>
            Готовы перевести клуб в цифру?
          </h2>
          <p className={s.sectionLead} style={{ margin: "0 auto 24px" }}>
            Зарегистрируйтесь и получите {TRIAL_DAYS} дней демо-доступа — клиенты
            увидят удобный кабинет, а вы полный контроль над занятиями и
            оплатами.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/register-clinic" className={`${s.btn} ${s.btnPrimary}`}>
              Попробовать бесплатно
            </Link>
            <Link to="/find" className={`${s.btn} ${s.btnOutline}`}>
              Войти в клуб
            </Link>
          </div>
        </div>
      </section>

      <footer className={s.footer}>
        © {new Date().getFullYear()} {L.productName}. CRM для спортивных клубов и
        тренеров.
      </footer>
    </div>
  );
}
